import React, { useState, useEffect } from 'react';
import { Users, ShoppingCart, TrendingUp, DollarSign, Clock, CheckCircle, Eye, EyeOff, Calendar, AlertTriangle, Sparkles, Activity } from 'lucide-react';
import PageHeader, { SectionLabel } from './ui-shared/PageHeader';
import QuickAnalytics from './QuickAnalytics';
import { GeographyMini } from './analytics/GeographyBlock';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Some browser extensions intercept fetch streams and break them with
// "DataCloneError: postMessage Request could not be cloned". XHR has no
// such issue — same workaround we use for Excel imports.
const xhrGetJSON = (url, token) => new Promise((resolve, reject) => {
  const xhr = new XMLHttpRequest();
  xhr.open('GET', url);
  xhr.setRequestHeader('Authorization', `Bearer ${token}`);
  xhr.responseType = 'json';
  xhr.onload = () => {
    if (xhr.status >= 200 && xhr.status < 300) resolve(xhr.response || {});
    else reject(new Error(`HTTP ${xhr.status}`));
  };
  xhr.onerror = () => reject(new Error('Network error'));
  xhr.send();
});

export default function AdminDashboard({ token, setActivePage, navigateWithFilter }) {
  const [stats, setStats] = useState({
    customers: {},
    orders: {},
    transactions: {}
  });
  const [loading, setLoading] = useState(true);
  // SLA-overdue tickets — admin owes a reply for >4h. Refresh every 60s
  // so the card gets dismissed (or re-surfaces) without a manual reload.
  const [sla, setSla] = useState({ count: 0, oldest_hours: 0, items: [], hours: 4 });
  // Smart Vehicle Request counter — surfaces customer-typed "I can't find
  // my model" requests waiting for the admin to promote them into the
  // price catalog. Re-fetched on the same 60s clock as the SLA counter.
  const [smartPending, setSmartPending] = useState({ pending: 0, promoted: 0 });
  // Per-widget hide flags (persisted in localStorage so admin's choice survives refresh)
  const [hideTodayOrders,    setHideTodayOrders]    = useState(() => localStorage.getItem('admin.hideTodayOrders') === '1');
  const [hideTodayRecharged, setHideTodayRecharged] = useState(() => localStorage.getItem('admin.hideTodayRecharged') === '1');

  const toggleHide = (which) => {
    if (which === 'orders') {
      const next = !hideTodayOrders;
      setHideTodayOrders(next);
      localStorage.setItem('admin.hideTodayOrders', next ? '1' : '0');
    } else {
      const next = !hideTodayRecharged;
      setHideTodayRecharged(next);
      localStorage.setItem('admin.hideTodayRecharged', next ? '1' : '0');
    }
  };

  useEffect(() => {
    fetchAllStats();
    fetchSla();
    fetchSmartPending();
    const id = setInterval(() => { fetchSla(); fetchSmartPending(); }, 60000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSmartPending = async () => {
    try {
      const [p, q] = await Promise.all([
        xhrGetJSON(`${API_URL}/api/orders/count?smart_vehicle=pending`, token),
        xhrGetJSON(`${API_URL}/api/orders/count?smart_vehicle=promoted`, token),
      ]);
      setSmartPending({
        pending: Number(p?.total || 0),
        promoted: Number(q?.total || 0),
      });
    } catch (_) { /* silent — card just won't show a counter */ }
  };

  const fetchSla = async () => {
    try {
      // No `?hours=` — backend uses the configured threshold from
      // app_settings.ticket_sla_alert (4h default). Honors the
      // global enable/disable toggle and the per-ticket dismiss flag.
      const data = await xhrGetJSON(`${API_URL}/api/orders/tickets/sla-overdue`, token);
      setSla({
        enabled: data?.enabled !== false,
        count: data?.count || 0,
        oldest_hours: data?.oldest_hours || 0,
        items: Array.isArray(data?.items) ? data.items : [],
        hours: data?.hours || 4,
      });
    } catch (_) { /* silent — red card just won't show */ }
  };

  // Dismiss one ticket as "no reply needed". Optimistic update — drop
  // it from the list immediately so the click feels instant, then
  // refresh the full list to capture any concurrent changes.
  const dismissSla = async (orderId) => {
    try {
      setSla((s) => ({
        ...s,
        items: s.items.filter((t) => t.order_id !== orderId),
        count: Math.max(0, (s.count || 0) - 1),
      }));
      await fetch(`${API_URL}/api/orders/${orderId}/ticket-sla/dismiss`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchSla();
    } catch (_) { /* rolled back on next refresh */ }
  };

  const fetchAllStats = async () => {
    try {
      const [customersData, ordersData, transactionsData] = await Promise.all([
        xhrGetJSON(`${API_URL}/api/admin/customers/stats`, token),
        xhrGetJSON(`${API_URL}/api/orders/stats`, token),
        xhrGetJSON(`${API_URL}/api/transactions/stats`, token),
      ]);
      setStats({
        customers: customersData || {},
        orders: ordersData || {},
        transactions: transactionsData || {},
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const go = (page) => { if (setActivePage) setActivePage(page); };

  if (loading) {
    return <div className="flex items-center justify-center h-64">
      <div className="text-lg text-slate-600">Loading dashboard...</div>
    </div>;
  }

  return (
    <div className="p-4 md:p-6 w-full mx-auto" data-testid="admin-dashboard">
      <PageHeader
        icon={<Activity className="w-5 h-5" />}
        title="Dashboard"
        subtitle="Operational overview · alerts, KPIs and today's flow"
        meta="Live SLA and smart-vehicle counters refresh every 60s"
        testid="admin-dashboard-header"
      />

      {/* ============================  ALERTS  ============================ */}
      {((sla.enabled !== false && sla.count > 0) || smartPending.pending > 0) && (
        <SectionLabel testid="admin-dash-section-alerts">Alerts</SectionLabel>
      )}

      {/* SLA-violation banner — shown only when the alert is enabled in
          Settings → System → Alerts (otherwise sla.enabled=false and we
          skip rendering entirely) AND at least one ticket has waited
          longer than the configured threshold without an admin reply.
          Each ticket gets its own "Dismiss — no reply needed" button:
          one click marks it as ticket_sla_dismissed_at = now and hides
          it until the customer writes again. Stays loud (red + pulsing
          dot) so it's the first thing admins see on login. */}
      {sla.enabled !== false && sla.count > 0 && (
        <div
          data-testid="admin-sla-alert"
          className="w-full mb-4 p-3 md:p-4 rounded-lg border-2 border-red-400 bg-gradient-to-r from-red-50 to-rose-50 shadow-sm"
        >
          <div className="flex items-start gap-3">
            <div className="relative flex-shrink-0">
              <div className="w-10 h-10 rounded-full bg-red-500 text-white flex items-center justify-center shadow-md">
                <AlertTriangle className="w-5 h-5" />
              </div>
              <span className="absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full bg-red-500 animate-ping opacity-75" />
            </div>
            <div className="flex-1 min-w-0">
              <button
                type="button"
                onClick={() => go('tickets')}
                className="text-left hover:underline focus:outline-none focus:underline"
                data-testid="admin-sla-alert-headline"
              >
                <p className="text-sm md:text-base font-bold text-red-800 leading-tight">
                  SLA violation · {sla.count} ticket{sla.count === 1 ? '' : 's'} waiting &gt;{sla.hours}h
                </p>
                <p className="text-xs md:text-sm text-red-700/80 mt-0.5">
                  Oldest: <span className="font-semibold font-mono tabular-nums">{sla.oldest_hours}h</span> without an admin reply. Click to open Tickets →
                </p>
              </button>
              {sla.items.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {sla.items.slice(0, 5).map((t) => (
                    <span
                      key={t.order_id}
                      data-testid={`sla-item-${t.order_id}`}
                      className="inline-flex items-stretch overflow-hidden rounded border border-red-200 bg-white/70 text-[11px] font-mono text-red-800"
                    >
                      <button
                        type="button"
                        onClick={() => go('tickets')}
                        className="px-2 py-0.5 hover:bg-red-50 focus:outline-none focus:bg-red-50"
                        title={`Open order #${t.order_number}`}
                      >
                        #{t.order_number}
                        <span className="text-red-500"> · {t.hours_overdue}h</span>
                      </button>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); dismissSla(t.order_id); }}
                        title="Dismiss — no reply needed (will resurface if customer writes again)"
                        data-testid={`sla-dismiss-${t.order_id}`}
                        className="px-1.5 border-l border-red-200 text-red-500 hover:text-red-800 hover:bg-red-100 focus:outline-none focus:bg-red-100"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                  {sla.items.length > 5 && (
                    <button
                      type="button"
                      onClick={() => go('tickets')}
                      className="inline-flex items-center px-2 py-0.5 text-[11px] text-red-700 font-semibold hover:underline focus:outline-none focus:underline"
                    >
                      +{sla.items.length - 5} more
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ============================  MAIN KPI  ============================ */}
      <SectionLabel testid="admin-dash-section-kpi" hint="Cumulative — all time">Main KPIs</SectionLabel>
      {/* Main Stats — compact. Icons were w-12 h-12 + text-3xl values in
          p-6 cards; now w-8 h-8 icons + text-2xl values in p-3 cards so 4
          cards fit in a single compact row on desktop and 2×2 on mobile.
          Each card navigates to the matching admin page (with pre-applied
          status filter where appropriate). */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        {/* Total Customers */}
        <button
          type="button"
          onClick={() => go('customers')}
          data-testid="admin-dash-total-customers"
          title="Active + inactive customer accounts (legacy imported customers included). +N wk shows new registrations in the last 7 days."
          className="group text-left bg-gradient-to-br from-cyan-500 to-blue-600 text-white p-3 rounded-lg shadow-sm hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 transition-all focus:outline-none focus:ring-2 focus:ring-white/40"
        >
          <div className="flex items-center justify-between mb-1.5">
            <Users className="w-6 h-6 opacity-80" />
            <span className="text-[10px] uppercase tracking-wider opacity-80">+{stats.customers.new_customers_week || 0} wk</span>
          </div>
          <p className="text-[11px] opacity-90 leading-tight">Total Customers</p>
          <p className="text-2xl font-bold leading-tight">{stats.customers.total_customers || 0}</p>
        </button>

        {/* Open Orders — jump to orders page pre-filtered to status "open" */}
        <button
          type="button"
          onClick={() => (navigateWithFilter ? navigateWithFilter('orders', { ordersStatus: 'open' }) : go('orders'))}
          data-testid="admin-dash-open-orders"
          title="Orders currently in `open` status, waiting for engineer pickup. The chip shows how many are already in `working`. Click to open the Orders list pre-filtered to Open."
          className="group text-left bg-gradient-to-br from-amber-500 to-orange-600 text-white p-3 rounded-lg shadow-sm hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 transition-all focus:outline-none focus:ring-2 focus:ring-white/40"
        >
          <div className="flex items-center justify-between mb-1.5">
            <Clock className="w-6 h-6 opacity-80" />
            <span className="text-[10px] uppercase tracking-wider opacity-80">{stats.orders.working || 0} working</span>
          </div>
          <p className="text-[11px] opacity-90 leading-tight">Open Orders</p>
          <p className="text-2xl font-bold leading-tight">{stats.orders.open || 0}</p>
        </button>

        {/* Completed Orders — jump to orders page pre-filtered to "completed" */}
        <button
          type="button"
          onClick={() => (navigateWithFilter ? navigateWithFilter('orders', { ordersStatus: 'completed' }) : go('orders'))}
          data-testid="admin-dash-completed-orders"
          title="All-time completed orders — both current and legacy. Includes orders closed automatically after delivery."
          className="group text-left bg-gradient-to-br from-green-500 to-emerald-600 text-white p-3 rounded-lg shadow-sm hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 transition-all focus:outline-none focus:ring-2 focus:ring-white/40"
        >
          <div className="flex items-center justify-between mb-1.5">
            <CheckCircle className="w-6 h-6 opacity-80" />
            <span className="text-[10px] uppercase tracking-wider opacity-80">all time</span>
          </div>
          <p className="text-[11px] opacity-90 leading-tight">Completed</p>
          <p className="text-2xl font-bold leading-tight">{stats.orders.completed || 0}</p>
        </button>

        {/* Total Revenue — jump to transactions */}
        <button
          type="button"
          onClick={() => go('transactions')}
          data-testid="admin-dash-total-recharged"
          title="Lifetime real money received (Stripe + PayPal + manual top-ups). Bonus credits and reconciliation entries are EXCLUDED. Click to open Transactions."
          className="group text-left bg-gradient-to-br from-purple-500 to-pink-600 text-white p-3 rounded-lg shadow-sm hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 transition-all focus:outline-none focus:ring-2 focus:ring-white/40"
        >
          <div className="flex items-center justify-between mb-1.5">
            <DollarSign className="w-6 h-6 opacity-80" />
            <span className="text-[10px] uppercase tracking-wider opacity-80">{stats.transactions.total_transactions || 0} tx</span>
          </div>
          <p className="text-[11px] opacity-90 leading-tight">Total Recharged</p>
          <p className="text-2xl font-bold leading-tight">€{stats.transactions.total_recharged?.toFixed(0) || 0}</p>
        </button>
      </div>


      {/* Smart Vehicle Requests — pending promotion to catalog. Hidden when
         there's nothing to act on so the dashboard doesn't carry a noisy
         zero counter. Cyan when there's work to do; the secondary count
         shows how many were already promoted (for context). */}
      {smartPending.pending > 0 && (
        <div className="mb-4">
          <button
            type="button"
            onClick={() => (navigateWithFilter
              ? navigateWithFilter('orders', { ordersSmart: 'pending' })
              : go('orders'))}
            data-testid="admin-dash-smart-pending"
            className="w-full text-left bg-gradient-to-r from-cyan-500 to-sky-600 text-white p-3 rounded-lg shadow-sm hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 transition-all focus:outline-none focus:ring-2 focus:ring-white/40"
          >
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2.5 min-w-0">
                <Sparkles className="w-6 h-6 opacity-90 shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-bold leading-tight">
                    Smart Vehicle requests pending
                  </p>
                  <p className="text-[11px] opacity-90 leading-tight">
                    Customers asked for a vehicle that isn't in the price catalog yet — review &amp; promote.
                  </p>
                </div>
              </div>
              <div className="text-right shrink-0">
                <p className="text-3xl font-bold leading-tight" data-testid="admin-dash-smart-pending-count">
                  {smartPending.pending}
                </p>
                <p className="text-[10px] opacity-80">
                  {smartPending.promoted} promoted
                </p>
              </div>
            </div>
          </button>
        </div>
      )}

      {/* ============================  TODAY  ============================ */}
      <SectionLabel testid="admin-dash-section-today" hint="Resets at 00:00 UTC">Today</SectionLabel>
      {/* Today widgets — admin can hide each via the eye icon. Same compact proportions. */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4" data-testid="admin-today-widgets">
        {/* Today's orders amount */}
        <div
          className="relative bg-gradient-to-br from-indigo-500 to-violet-600 text-white p-3 rounded-lg shadow-sm overflow-hidden"
          title="Sum of order totals placed since 00:00 UTC. Includes only real orders (not synthetic legacy imports)."
        >
          <button
            type="button"
            onClick={() => toggleHide('orders')}
            data-testid="toggle-today-orders"
            title={hideTodayOrders ? 'Show value' : 'Hide value'}
            className="absolute top-2 right-2 w-6 h-6 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center transition-colors"
          >
            {hideTodayOrders ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
          </button>
          <div className="flex items-center gap-3 pr-7">
            <Calendar className="w-6 h-6 opacity-80 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-[11px] opacity-90 leading-tight">Orders today</p>
              <p className="text-xl font-bold leading-tight truncate" data-testid="today-orders-amount">
                {hideTodayOrders ? '€••••' : `€${(stats.orders.today_orders_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
              </p>
              <p className="text-[10px] opacity-80">
                {hideTodayOrders ? '— — —' : `${stats.orders.today_orders_count || 0} orders today`}
              </p>
            </div>
          </div>
        </div>

        {/* Today's recharges (excluding bonus) */}
        <div
          className="relative bg-gradient-to-br from-emerald-500 to-teal-600 text-white p-3 rounded-lg shadow-sm overflow-hidden"
          title="Real €  received today (Stripe + PayPal + manual). Excludes bonus credits and reconciliation entries."
        >
          <button
            type="button"
            onClick={() => toggleHide('recharged')}
            data-testid="toggle-today-recharged"
            title={hideTodayRecharged ? 'Show value' : 'Hide value'}
            className="absolute top-2 right-2 w-6 h-6 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center transition-colors"
          >
            {hideTodayRecharged ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
          </button>
          <div className="flex items-center gap-3 pr-7">
            <DollarSign className="w-6 h-6 opacity-80 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-[11px] opacity-90 leading-tight">Recharges today (real €)</p>
              <p className="text-xl font-bold leading-tight truncate" data-testid="today-recharged-amount">
                {hideTodayRecharged ? '€••••' : `€${(stats.transactions.today_recharged_no_bonus || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
              </p>
              <p className="text-[10px] opacity-80">
                {hideTodayRecharged ? '— — —' : `${stats.transactions.today_recharged_count || 0} tx · bonuses excluded`}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ============================  BALANCES  ============================ */}
      <SectionLabel testid="admin-dash-section-balances">Balances &amp; activity</SectionLabel>
      {/* Secondary Stats — clickable, route to the matching admin page. */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <button onClick={() => go('customers')} data-testid="admin-stat-total-balance"
          title="Sum of all customers' available balances (real € + bonus credits, less reserved). Live."
          className="text-left bg-white p-3 rounded-lg border border-slate-200 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-md bg-blue-100 flex items-center justify-center flex-shrink-0">
              <DollarSign className="w-4 h-4 text-blue-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[11px] text-slate-500 uppercase tracking-wider">Total Balance</p>
              <p className="text-lg font-bold text-slate-800 leading-tight">€{stats.customers.total_balance?.toFixed(2) || '0.00'}</p>
              <p className="text-[10px] text-slate-400">Click to open customers →</p>
            </div>
          </div>
        </button>

        <button onClick={() => go('transactions')} data-testid="admin-stat-reserved"
          title="Funds currently locked on open/working orders. Released back to balance when an order is delivered or cancelled."
          className="text-left bg-white p-3 rounded-lg border border-slate-200 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-md bg-amber-100 flex items-center justify-center flex-shrink-0">
              <Clock className="w-4 h-4 text-amber-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[11px] text-slate-500 uppercase tracking-wider">Reserved</p>
              <p className="text-lg font-bold text-slate-800 leading-tight">€{stats.customers.total_reserved?.toFixed(2) || '0.00'}</p>
              <p className="text-[10px] text-slate-400">Blocked for active orders →</p>
            </div>
          </div>
        </button>

        <button onClick={() => go('orders')} data-testid="admin-stat-new"
          title="Orders and transactions created in the last 24 hours. Click to open the Orders list."
          className="text-left bg-white p-3 rounded-lg border border-slate-200 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-md bg-cyan-100 flex items-center justify-center flex-shrink-0">
              <TrendingUp className="w-4 h-4 text-cyan-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[11px] text-slate-500 uppercase tracking-wider">New (24h)</p>
              <p className="text-lg font-bold text-slate-800 leading-tight">{stats.orders.new_orders || 0}</p>
              <p className="text-[10px] text-slate-400">{stats.transactions.new_transactions || 0} transactions</p>
            </div>
          </div>
        </button>
      </div>

      {/* Quick Actions — slim pill row instead of the old huge grid. */}
      <div className="bg-white p-3 rounded-lg border border-slate-200 shadow-sm">
        <h3 className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-2">Quick actions</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <button onClick={() => go('customers')} data-testid="admin-qa-customers"
            className="flex items-center gap-2 px-3 py-2 border border-slate-200 rounded-md hover:border-cyan-500 hover:bg-cyan-50 transition-all text-left">
            <Users className="w-4 h-4 text-cyan-600 flex-shrink-0" />
            <span className="text-xs font-semibold text-slate-700">Customers</span>
          </button>
          <button onClick={() => go('orders')} data-testid="admin-qa-orders"
            className="flex items-center gap-2 px-3 py-2 border border-slate-200 rounded-md hover:border-blue-500 hover:bg-blue-50 transition-all text-left">
            <ShoppingCart className="w-4 h-4 text-blue-600 flex-shrink-0" />
            <span className="text-xs font-semibold text-slate-700">Orders</span>
          </button>
          <button onClick={() => go('transactions')} data-testid="admin-qa-txns"
            className="flex items-center gap-2 px-3 py-2 border border-slate-200 rounded-md hover:border-green-500 hover:bg-green-50 transition-all text-left">
            <DollarSign className="w-4 h-4 text-green-600 flex-shrink-0" />
            <span className="text-xs font-semibold text-slate-700">Transactions</span>
          </button>
          <button onClick={() => go('tickets')} data-testid="admin-qa-tickets"
            className="flex items-center gap-2 px-3 py-2 border border-slate-200 rounded-md hover:border-purple-500 hover:bg-purple-50 transition-all text-left">
            <TrendingUp className="w-4 h-4 text-purple-600 flex-shrink-0" />
            <span className="text-xs font-semibold text-slate-700">Tickets</span>
          </button>
        </div>
      </div>

      {/* Quick analytics — at-a-glance KPIs + sparklines for today and
          the last 7 days. Tap-through opens the full Analytics page. */}
      <QuickAnalytics
        token={token}
        onOpenAnalytics={() => go('analytics')}
        navigateWithFilter={navigateWithFilter}
        go={go}
      />

      {/* Geography snapshot — top 5 countries by orders (30 days).
          Full breakdown (donut + all countries + revenue/customers
          axes) lives on the Analytics page. */}
      <GeographyMini token={token} onOpenAnalytics={() => go('analytics')} />
    </div>
  );
}
